import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import InternalHeader from '../InternalHeader';
import Sidebar from '../Sidebar';
import '../Patients/PatientProfile.css';
import AccessControl from '../AccessControl';
import LinkMap from '../LinkMap/LinkMap';
import { FaTrash, FaPlus } from 'react-icons/fa6';
import { FaSave } from 'react-icons/fa';
import DataTable from 'react-data-table-component';

const DentistProfile = () => {
    const { id } = useParams();
    const [dentist, setDentist] = useState(null);
    const [loading, setLoading] = useState(true);
    const [isSidebarVisible, setIsSidebarVisible] = useState(true);
    const [isEditable, setIsEditable] = useState(false);
    const [successModalVisible, setSuccessModalVisible] = useState(false);
    const [internalUsers, setInternalUsers] = useState([]);
    const linkData = [
        { label: 'Dentists', path: '/dentists' },
        { label: 'Dentist Profile', path: '/dentist-profile' },
    ];
    const [activeTab, setActiveTab] = useState("Treatments");
    const [invoiceItems, setInvoiceItems] = useState([]);
    const [discountPercent, setDiscountPercent] = useState(0);
    const [inventoryItems, setInventoryItems] = useState([]);
    const [treatments, setTreatments] = useState([]);
    const [inventoryData, setInventoryData] = useState([]);

    const handleTabClick = (tabName) => {
        setActiveTab(tabName);
    };

    const handleSaveInvoice = async () => {
        try {
            const response = await fetch(`https://api.hgcdental.clinic/api/dentists/${id}/treatments`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({
                    treatments: invoiceItems
                        .filter(item => item.treatmentId) // Only include items with treatment selected
                        .map(item => ({
                            treatmentId: item.treatmentId,
                            quantity: item.quantity
                        }))
                })
            });
    
            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(errorData.message || 'Failed to save treatments');
            }
    
            const data = await response.json();
            console.log('Save successful:', data);
            alert('Treatments saved successfully!');
            setIsEditable(false); // Exit edit mode after save
    
        } catch (error) {
            console.error('Save failed:', error);
            alert(`Error: ${error.message}`);
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [dentistRes, usersRes, treatmentsRes] = await Promise.all([
                    fetch(`https://api.hgcdental.clinic/api/dentists/${id}`),
                    fetch('https://api.hgcdental.clinic/api/internalusers'),
                    fetch('https://api.hgcdental.clinic/api/treatments')
                ]);

                if (!dentistRes.ok || !usersRes.ok || !treatmentsRes.ok) throw new Error('Failed to fetch data');

                const dentistData = await dentistRes.json();
                const usersData = await usersRes.json();
                const treatmentsData = await treatmentsRes.json();

                // Convert ObjectId to string for the dropdown
                if (dentistData.internalUser) {
                    dentistData.internalUser = dentistData.internalUser._id
                        ? dentistData.internalUser._id.toString()
                        : dentistData.internalUser.toString();
                }

                setDentist(dentistData);
                setInternalUsers(usersData);
                setTreatments(treatmentsData);
                
                // Load dentist's existing treatments if any
                if (dentistData.treatments && dentistData.treatments.length > 0) {
                    const initialItems = dentistData.treatments.map(treatment => ({
                        treatmentId: treatment.treatmentId._id,
                        legend: treatment.treatmentId.Legend,
                        treatmentName: treatment.treatmentId.Treatmentname,
                        treatmentCost: treatment.treatmentId.Cost,
                        quantity: treatment.quantity,
                        total: treatment.treatmentId.Cost * treatment.quantity,
                    }));
                    setInvoiceItems(initialItems);
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const invoiceColumns = [
        {
            name: 'Treatment Name',
            cell: (row, index) => (
                <select
                    value={row.treatmentId || ''}
                    onChange={(e) => handleTreatmentChange(index, e.target.value)}
                    disabled={!isEditable}
                >
                    <option value="" disabled>
                        Select Treatment
                    </option>
                    {treatments.map((treatment) => (
                        <option key={treatment._id} value={treatment._id}>
                            {treatment.Treatmentname}
                        </option>
                    ))}
                </select>
            ),
        }
    ];

    const customStyles = {
        rows: {
            style: {
                borderBottom: '1px solid #ddd',
            },
        },
        headCells: {
            style: {
                backgroundColor: '#007bff',
                color: 'white',
                fontWeight: 'bold',
                padding: '15px 10px',
                textAlign: 'center',
            },
        },
        cells: {
            style: {
                padding: '12px 10px',
                borderRight: '1px solid #ddd',
            },
        },
        pagination: {
            style: {
                display: 'flex',
                justifyContent: 'center',
                padding: '10px',
                backgroundColor: '#f4f4f4',
            },
        },
        sortIcon: {
            style: {
                color: '#007bff',
                fontSize: '18px',
            },
        },
    };

    const handleDeleteItem = (index) => {
        const updatedItems = invoiceItems.filter((_, i) => i !== index);
        setInvoiceItems(updatedItems);
    };

    const handleTreatmentChange = (index, treatmentId) => {
        const selectedTreatment = treatments.find(t => t._id === treatmentId);
        if (selectedTreatment) {
            const updatedItems = invoiceItems.map((item, i) =>
                i === index
                    ? {
                        ...item,
                        treatmentId,
                        legend: selectedTreatment.Legend,
                        treatmentName: selectedTreatment.Treatmentname,
                        treatmentCost: selectedTreatment.Cost,
                        quantity: 1,
                        total: selectedTreatment.Cost * 1,
                    }
                    : item
            );
            setInvoiceItems(updatedItems);
        }
    };

    const handleQuantityChange = (index, quantity) => {
        if (quantity < 1) quantity = 1;
        
        const updatedItems = invoiceItems.map((item, i) =>
            i === index
                ? {
                    ...item,
                    quantity,
                    total: item.treatmentCost * quantity,
                }
                : item
        );
        setInvoiceItems(updatedItems);
    };

    const addInvoiceItem = () => {
        if (!isEditable) return;
        
        setInvoiceItems([
            ...invoiceItems,
            {
                treatmentId: null,
                legend: '',
                treatmentName: '',
                treatmentCost: 0,
                quantity: 1,
                total: 0,
            },
        ]);
    };

    // Fetch internal users from the backend
    useEffect(() => {
        const fetchInternalUsers = async () => {
            try {
                const response = await fetch('https://api.hgcdental.clinic/api/internalusers');
                const data = await response.json();
                if (response.ok) {
                    setInternalUsers(data);
                } else {
                    console.error('Error fetching internal users:', data.error);
                }
            } catch (error) {
                console.error('Error:', error);
            }
        };
        fetchInternalUsers();
    }, []);

    const handleSaveClick = async () => {
        try {
            const response = await fetch(`https://api.hgcdental.clinic/api/dentists/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...dentist,
                    internalUser: dentist.internalUser || null
                }),
            });

            const result = await response.json();

            if (!response.ok) {
                if (response.status === 400 && result.message.includes('already assigned')) {
                    alert(`Error: ${result.message}\nAssigned to dentist ID: ${result.assignedTo}`);
                } else {
                    throw new Error(result.message || 'Failed to update dentist');
                }
                return;
            }

            setDentist(result);
            setIsEditable(false);
            setSuccessModalVisible(true);
        } catch (error) {
            console.error(error);
            alert(error.message);
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setDentist((prev) => ({ ...prev, [name]: value }));
    };

    const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);
    const handleEditClick = () => setIsEditable(true);
    const closeSuccessModal = () => setSuccessModalVisible(false);

    if (loading) return <div>Loading...</div>;
    if (!dentist) return <div>No dentist found</div>;

    return (
        <AccessControl>
            <div className={`create-patient-container ${isSidebarVisible ? 'sidebar-visible' : 'sidebar-hidden'}`}>
                <InternalHeader moduleName="Dentists" toggleSidebar={toggleSidebar} />
                <div className="create-patient-content">
                    <Sidebar isSidebarVisible={isSidebarVisible} />
                    <div className="create-patient">
                        <LinkMap
                            links={linkData}
                            moduleName="Category"
                            moduleIcon="doctor"
                        />
                        <div>
                            {isEditable ? (
                                <button className="create-patient-button" onClick={handleSaveClick}>Save</button>
                            ) : (
                                <button className="create-patient-button" onClick={handleEditClick}>Edit</button>
                            )}
                        </div>
                        <div className="create-patient-form">
                            <h2>Dentist Profile</h2>

                            {/* Form Fields */}
                            <div className="input-group">
                                <label className="label">Dentist ID</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={dentist.dentistID || ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled
                                />
                            </div>

                            <div className="input-group">
                                <label className="label">First Name</label>
                                <input
                                    type="text"
                                    name="firstName"
                                    value={dentist.firstName || ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                />
                            </div>

                            <div className="input-group">
                                <label className="label">Middle Name</label>
                                <input
                                    type="text"
                                    name="middleName"
                                    value={dentist.middleName || ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                />
                            </div>

                            <div className="input-group">
                                <label className="label">Last Name</label>
                                <input
                                    type="text"
                                    name="lastName"
                                    value={dentist.lastName || ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                />
                            </div>

                            <div className="input-group">
                                <label className="label">Sex</label>
                                <select
                                    name="sex"
                                    value={dentist.sex || ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                >
                                    <option value="">Select</option>
                                    <option value="Male">Male</option>
                                    <option value="Female">Female</option>
                                </select>
                            </div>

                            <div className="input-group">
                                <label className="label">Date of Birth</label>
                                <input
                                    type="date"
                                    name="birthdate"
                                    value={dentist.birthdate ? new Date(dentist.birthdate).toISOString().split('T')[0] : ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                />
                            </div>

                            <div className="input-group">
                                <label className="label">Address</label>
                                <input
                                    type="text"
                                    name="address"
                                    value={dentist.address || ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                />
                            </div>

                            <div className="input-group">
                                <label className="label">Contact Information</label>
                                <input
                                    type="text"
                                    name="contactNumber"
                                    value={dentist.ContactInformation || ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                />
                            </div>

                            <div className="input-group">
                                <label className="label">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={dentist.email || ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                />
                            </div>

                            <div className="input-group">
                                <label className="label">User Account</label>
                                <select
                                    name="internalUser"
                                    value={dentist.internalUser?._id || dentist.internalUser || ''}
                                    onChange={handleInputChange}
                                    className="input-field"
                                    disabled={!isEditable}
                                >
                                    <option value="">Select</option>
                                    {internalUsers.map((user) => (
                                        <option
                                            key={user._id}
                                            value={user._id}
                                        >
                                            {user.firstName} {user.lastName} ({user.email})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Tab Navigation */}
                            <div className="tab-navigation">
                                <span
                                    onClick={() => handleTabClick('Treatments')}
                                    className={`tab ${activeTab === 'Treatments' ? 'active' : ''}`}
                                >
                                    Treatments
                                </span>
                            </div>

                            {/* Tab Content */}
                            <div className="tab-content">
                                {activeTab === 'Treatments' && (
                                    <div className="invoice-tab">
                                        {isEditable && (
                                            <>
                                                <button onClick={addInvoiceItem} className="create-button-table">
                                                    <FaPlus /> Add Treatment
                                                </button>
                                                <button onClick={handleSaveInvoice} className="create-button-table">
                                                    <FaSave /> Save Treatments
                                                </button>
                                                <div className="modal-divider-table" />
                                            </>
                                        )}
                                        <div className="table-form-table">
                                            <DataTable
                                                columns={invoiceColumns}
                                                data={invoiceItems}
                                                highlightOnHover
                                                striped
                                                customStyles={customStyles}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Success Modal */}
                            {successModalVisible && (
                                <div className="modal-overlay" onClick={closeSuccessModal}>
                                    <div className="modal success-modal" onClick={(e) => e.stopPropagation()}>
                                        <h3>Success!</h3>
                                        <div className="modal-divider" />
                                        <p>Dentist profile updated successfully.</p>
                                        <div className="modal-divider" />
                                        <button className="confirm-button" onClick={closeSuccessModal}>Close</button>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </AccessControl>
    );
};

export default DentistProfile;